import { createTheme, ThemeProvider } from '@mui/material';
import { Amplify } from 'aws-amplify';
import ReactDOM from 'react-dom/client';
import './assets/styles/index.css';
import { AmplifyApiClientProvider } from './providers/AmplifyApiClientProvider';
import { AuthProvider } from './providers/AuthProvider';
import Router from './router';
import config from '@/config';
import { ApolloProviderWrapper } from './client/client';
import ReactGA from 'react-ga4';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Provider as RollbarProvider, ErrorBoundary } from '@rollbar/react';
import { CookieStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

const theme = createTheme({
  typography: {
    fontFamily: [
      'Realist',
      'Helvetica Neue',
      'Noto Sans',
      'Liberation Sans',
      'Arial, sans-serif',
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
      'Noto Color Emoji'
    ].join(',')
  },
  palette: {
    primary: {
      main: '#99D9D9'
    },
    error: {
      main: '#DB0632'
    }
  }
});

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.aws.cognito.userPoolId,
      userPoolClientId: config.aws.cognito.userPoolClientId,
      identityPoolId: config.aws.cognito.identityPoolId,
      allowGuestAccess: true
    }
  },
  API: {
    GraphQL: {
      // Fun fact: If you don't include the region, Queries and Mutations work but Subscriptions do not.
      //           I have never questioned my sanity more than when I was debugging this. Absolutely
      //           zero-documentation on this behavior and this field is not required! I need a tylenol!
      //           ~ Kyle Lierer, circa 2024
      region: 'us-east-2',
      endpoint: config.domain.http,
      defaultAuthMode: 'userPool'
    }
  }
});

cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    path: '/',
    sameSite: 'lax', // 'lax' is usually sufficient; use 'strict' if possible
    domain: window.location.hostname === 'localhost' ? 'localhost' : `.${config.siteDomain}`,
    secure: window.location.hostname !== 'localhost',
    expires: 365 // Cookie expiration in days
  })
);

ReactGA.initialize(config.googleAnalyticsId);

const rollbarConfig = config.rollbar.enabled
  ? {
      accessToken: config.rollbar.accessToken,
      environment: config.rollbar.environment,
      captureUncaught: true,
      captureUnhandledRejections: true,
      payload: {
        client: {
          javascript: {
            source_map_enabled: true
          }
        }
      }
    }
  : null;

const appTree = (
  <ThemeProvider theme={theme}>
    <AuthProvider>
      <ApolloProviderWrapper>
        <AmplifyApiClientProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Router />
          </LocalizationProvider>
        </AmplifyApiClientProvider>
      </ApolloProviderWrapper>
    </AuthProvider>
  </ThemeProvider>
);

const RootApp = rollbarConfig ? (
  <RollbarProvider config={rollbarConfig}>
    <ErrorBoundary>{appTree}</ErrorBoundary>
  </RollbarProvider>
) : (
  appTree
);

ReactDOM.createRoot(document.getElementById('root')!).render(RootApp);
